import { createContext, useContext, useState, ReactNode } from 'react';

export type Lang = 'tr' | 'en';

// Türkçe metin -> İngilizce çeviri sözlüğü. Anahtar olan Türkçe metin bulunamazsa
// (henüz çevrilmemiş bir ekran) olduğu gibi (Türkçe) gösterilir — kırık/boş metin yerine.
// Bu taban sözlük uygulama çatısını (sidebar/topbar/sayfa başlıkları) kapsar;
// her ekran kendi metinlerini addTranslations() ile ayrıca ekleyebilir.
const DICT: Record<string, string> = {
  // Sidebar — Pixa alt menüsü
  'Siparişler': 'Orders',
  'Katalog': 'Catalog',
  'Stok': 'Stock',
  'İadeler': 'Returns',
  'Kargo': 'Shipping',
  'Faturalar': 'Invoices',
  'Sorular': 'Questions',
  // Sidebar — ana menü
  'Ana Sayfa': 'Home',
  'Asistan': 'Assistant',
  'Panolar': 'Boards',
  'Görevler': 'Tasks',
  'Notlar': 'Notes',
  'Faturalama': 'Billing',
  'Faturalandırma': 'Billing',
  'Uyarılar': 'Alerts',
  // Sidebar — bölüm başlıkları
  'Reklam Yönetimi': 'Ad Management',
  'Platform Karşılaştırma': 'Platform Comparison',
  // Pixa menüsü
  '— ÖZET': '— OVERVIEW', '— SATIŞ': '— SALES', '— KATALOG': '— CATALOG', '— SİSTEM': '— SYSTEM',
  'Pazaryeri Listeleri': 'Marketplace Listings', 'Değişiklik Geçmişi': 'Change History', 'Raporlar': 'Reports',
  'Veri & Analitik': 'Data & Analytics',
  'Araçlar': 'Tools',
  // Sidebar — alt öğeler / genel
  'Genel Bakış': 'Overview',
  'Kampanya Tipi': 'Campaign Type',
  'Reklam Metni': 'Ad Copy',
  'Gün/Saat': 'Day/Hour',
  'Gün / Saat': 'Day / Hour',
  'Kreatifler': 'Creatives',
  'Kampanyalar': 'Campaigns',
  'Bannerlar': 'Banners',
  'Sayfa Perf.': 'Page Perf.',
  'Sayfa Performansı': 'Page Performance',
  'Ürün Analizi': 'Product Analysis',
  'Trafik Kaynağı': 'Traffic Source',
  'Ürünler': 'Products',
  'Sorgular': 'Queries',
  'Sayfalar': 'Pages',
  'Optimizasyon': 'Optimization',
  'Otomasyon Şablonları': 'Automation Templates',
  'Optimizasyonlar': 'Optimizations',
  'Otomasyonlar': 'Automations',
  'Akış Oluşturucu': 'Flow Builder',
  'Kampanya Oluşturucu': 'Campaign Builder',
  'Akıllı Kitleler': 'Smart Audiences',
  'Aksiyon Logları': 'Action Logs',
  'Kredi Yükle': 'Add Credit',
  'Hesap': 'Account',
  'Destek': 'Support',
  'Kullanıcılar': 'Users',
  'Müşteri Yönetimi': 'Client Management',
  'Müşteriler': 'Clients',
  'Kreatif Stüdyo': 'Creative Studio',
  'Raporlama': 'Reporting',
  'Son İşlemler': 'Recent Activity',
  'Ayarlar': 'Settings',
  'Takım': 'Team',
  'Yönetim': 'Management',
  'Admin': 'Admin',
  'Kullanıcı': 'User',
  // Profil menüsü
  'Profil Ayarları': 'Profile Settings',
  'Çıkış Yap': 'Log Out',
  // Sidebar — çalışma alanı seçici
  'Çalışma Alanım': 'My Workspace',
  'Yeni Müşteri Ekle': 'Add New Client',
};

export function addTranslations(entries: Record<string, string>) {
  Object.assign(DICT, entries);
}

type LangCtx = { lang: Lang; setLang: (l: Lang) => void; t: (s: string) => string };

const LangContext = createContext<LangCtx>({ lang: 'tr', setLang: () => {}, t: (s) => s });

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    try { return (localStorage.getItem('poyais_lang') as Lang) || 'tr'; } catch { return 'tr'; }
  });
  const setLang = (l: Lang) => {
    setLangState(l);
    try { localStorage.setItem('poyais_lang', l); } catch {}
  };
  const t = (s: string) => (lang === 'en' ? (DICT[s] || s) : s);
  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}

export function useLang() {
  return useContext(LangContext);
}

export function LangSwitcher() {
  const { lang, setLang } = useLang();
  return (
    <div style={{ display: 'flex', background: 'var(--py-track-bg)', borderRadius: 8, padding: 2, gap: 2, flexShrink: 0 }}>
      {(['tr', 'en'] as Lang[]).map(l => (
        <button
          key={l}
          onClick={() => setLang(l)}
          style={{
            padding: '5px 9px',
            borderRadius: 6,
            border: 'none',
            background: lang === l ? 'var(--py-white)' : 'transparent',
            boxShadow: lang === l ? '0 1px 2px rgba(0,0,0,.15)' : 'none',
            color: lang === l ? 'var(--py-text)' : 'var(--py-text-dim)',
            fontSize: 11.5,
            fontWeight: 800,
            letterSpacing: '.02em',
            cursor: 'pointer',
            fontFamily: 'Manrope, sans-serif',
          }}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
